<template>
  <fts-design-modal
    full-screen
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <!-- studio title -->
      <div class="design-modal-header-std__title">
        <fts-emoji v-model="formData.emoji" class="design-modal-header-emoji" />
        <input
          type="text"
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{ error: $v.formData.name && $v.formData.name.$error }"
          v-model="formData.name"
          spellcheck="false"
          @input="debounceInput"
          ref="designerName"
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">
        {{ error("name") }}
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Listing</span>
      <span class="design-modal-header-description">
        filter entries and specify information to show different group of users
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- filter conditions -->
      <div class="design-modal-form-legend">Filter conditions</div>
      <!-- profile only -->
      <fts-form-element
        :item="{
          fieldName: 'profileOnly',
          input: 'toggleSwitch',
        }"
        :form-data="formData"
        comfort-design
      />
      <!-- filter conditions -->
      <template v-if="!formData.profileOnly">
        <div class="fts-form-group">
          <fts-query-designer
            :filter="formData.filter"
            :states="states"
            :information-source="filterSelection"
          />
          <!-- error message -->
          <div class="design-modal-form-error" v-if="error('filter')">
            {{ error("filter") }}
          </div>
        </div>
        <!-- query script -->
        <div class="fts-form-group">
          <fts-collapsible-element
            :condition="!!formData.queryScript"
            label="query script"
            immersive
            beta
          >
            <fts-script-editor
              slot="content"
              editor-id="queryScript"
              v-model="formData.queryScript"
              label="Query Script"
              lang="python"
            />
          </fts-collapsible-element>
        </div>
      </template>
      <!-- list design -->
      <div class="fts-form-group">
        <fts-list-designer
          v-model="formData.list"
          :information-source="information"
          :user-collection="userCollection"
          @input="updateDefaultFilter"
        />
      </div>
      <!-- list design (mobile) -->
      <div class="fts-form-group">
        <fts-list-designer
          title="Mobile list design"
          v-model="formData.mobileListDesign"
          :information-source="information"
          :user-collection="userCollection"
        />
      </div>
      <!-- advanced -->
      <fts-collapsible-element :condition="advanced" v-if="beta">
        <fts-btn
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          text
          text-transform-init
          :class="{ open: isVisible }"
          class="design-modal-advanced-toggle"
          @click="toggle()"
        >
          Advanced settings
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            class="design-modal-advanced-toggle-icon"
          />
        </fts-btn>
        <div slot="content" class="design-modal-advanced-content fts-row gutters">
          <div class="fts-col design-modal-form-legend">Default filters</div>
          <div class="fts-col fts-form-group">
            <fts-query-designer
              :filter="formData.defaultFilter"
              :states="states"
              :information-source="defaultFilterSelection"
            />
          </div>
        </div>
      </fts-collapsible-element>
    </template>
    <!-- footer left -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth -->
      <fts-auth
        type="menu"
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <!-- footer right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import changesets from "diff-json";

import commons from '../commons/commons';
import queryLogic from "../commons/queryLogic";
import validator from "../commons/designValidator";

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSQueryDesigner from "./FTSQueryDesigner";
import FTSListDesigner from "./FTSListDesigner";
import FTSFormElement from "../elements/FTSFormElement";
import FTSEmoji from "../elements/FTSEmoji";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSScriptEditor from "../design/FTSScriptEditor";
import FTSBtn from "../elements/FTSBtn";
import FTSContenteditable from "../elements/FTSContenteditable";

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-query-designer": FTSQueryDesigner,
    "fts-list-designer": FTSListDesigner,
    "fts-form-element": FTSFormElement,
    "fts-emoji": FTSEmoji,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-script-editor": FTSScriptEditor,
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
  },
  data: () => ({
    formData: null,
    originalData: null,
    resolve: null,
    saveCallBack: null,
    advanced: false,
  }),
  validations() {
    let formData = {};
    formData = validator.menuDesign(this.menus);
    return { formData };
  },
  methods: {
    async show(listing, saveCallBack) {
      this.saveCallBack = saveCallBack;
      this.formData = _.cloneDeep(listing);

      function expandListDesign(information, fields) {
        try {
          fields.forEach(x => {
            if (!x.system) {
              const informationItem = _.cloneDeep(information.find(y => y.id === x.id));
              if (informationItem && informationItem.displayName) {
                x.displayName = informationItem.displayName;
                if (
                  (informationItem.type == 14 || informationItem.type == 15) && 
                  Array.isArray(informationItem.fields)
                ) {
                  expandListDesign(informationItem.fields, x.fields);
                } else if (x.fields) {
                  delete x.fields;
                }
              }
            }
          });
        } catch(err) {
          console.error('ListingDesign:ExpandListDesign', err);
        }
      }

      if (!this.formData.filter) {
        this.$set(this.formData, "filter", { items: [] });
      }
      if (!this.formData.defaultFilter) {
        this.$set(this.formData, "defaultFilter", { items: [] });
      } else {
        this.updateDefaultFilter();
      }
      if (this.formData.list && Array.isArray(this.formData.list.columns)) {
        expandListDesign(this.information, this.formData.list.columns);
      }
      if (this.formData.mobileListDesign && Array.isArray(this.formData.mobileListDesign.columns)) {
        expandListDesign(this.information, this.formData.mobileListDesign.columns);
      }

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
        });
      });
    },
    done(saveOnly) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (saveOnly) {
          this.originalData = _.cloneDeep(this.formData);
          this.saveCallBack && this.saveCallBack(this.formData);
        } else {
          this.reset();
          this.resolve && this.resolve(this.formData);
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.reset();
              this.resolve && this.resolve(true);
            },
          },
        ],
      });
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      /* set route */
      if (this.$route.query.t || this.$route.query.tid) {
        let query = {...this.$route.query};
        delete query.t;
        delete query.tid;
        this.$router.push({ query: query })
      }
    },
    debounceInput: _.debounce(async function (target) {
      if (this.formData.name) {
        let vm = this;
        this.$store
          .dispatch("ai", {
            type: "emoji",
            text: vm.formData.name,
          })
          .then(function (response) {
            Vue.set(vm.formData, "emoji", response.data);
          });
      }
    }, 800),
    error(key) {
      return validator.menuDesignError(this.$v.formData, key);
    },
    recursiveProcess(items) {
      const filters = [...items];
      filters.forEach(x => {
        if (x.items && Array.isArray(x.items)) {
          this.recursiveProcess(x.items);
        } else {
          const item = this.defaultFilterSelection.find(y => y.id === x.fieldId);
          if (!item) {
            items.splice(items.indexOf(x), 1);
          }
        }
      });
    },
    updateDefaultFilter() {
      if (this.formData.defaultFilter && Array.isArray(this.formData.defaultFilter.items)) {
        this.recursiveProcess(this.formData.defaultFilter.items);
      }
    },
  },
  computed: {
    beta() {
      return this.$store.state.beta;
    },
    moduleName() {
      return this.$store.state.vector.name;
    },
    menus() {
      return this.$store.getters.canvasMenus;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    userCollection() {
      return (
        this.$store.state.vector && this.$store.state.vector.isUserCollection
      );
    },
    filterSelection() {
      return _.differenceWith(
        queryLogic.querySelectionPrefix,
        this.userCollection ? [] : ["username"],
        ({ id }, key) => id === key
      )
        .concat(
          this.information.filter(x => {
            const informationType = commons.informationTypes.find(y => y.id == x.type);
            return informationType && informationType.searchable;
          })
        )
        .concat(queryLogic.querySelectionSuffix);
    },
    defaultFilterSelection() {
      try {
        if (
          this.formData.list &&
          this.formData.list.columns &&
          this.formData.list.columns.length
        ) {
          return _.intersectionWith(
            this.filterSelection,
            [
              ...this.formData.list.columns,
              ...[
                { id: 'collaboration' },
              ],
            ],
            ({ id }, x) => id === x.id
          )
            .concat(queryLogic.querySelectionSuffix);
        }
      } catch(err) {
        console.error("ListingDesign:FilterSelection", err);
      }
      return this.filterSelection;
    },
    isExist() {
      return Boolean(this.menus.find(x => x.id === this.formData.id));
    },
  },
};
</script>